import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Descriptions, 
  Tag, 
  Space, 
  message, 
  Tabs, 
  Divider,
  Typography,
  Statistic,
  Row,
  Col,
  Timeline,
  Modal,
  Alert,
  Form,
  Input,
} from 'antd';

import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  DownloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  StopOutlined,
  ReloadOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  FileAddOutlined,
  LinkOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  UserAddOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { Skeleton, Tooltip, Empty } from 'antd';
import { useNavigate, useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { 
  getLicense, 
  deleteLicense, 
  validateLicense,
  generateOfflineLicense,
  bindMacAddress,
  getLicenseUsage,
  revokeLicense,
  getLicenseActivities,
  LICENSE_TYPES,
  LICENSE_STATUS,
  updateLicense
} from '../../services/licenseService';
import customerService from '../../services/customerService';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const LicenseDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState(null);
  const [usage, setUsage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [validating, setValidating] = useState(false);
  const [generatingOffline, setGeneratingOffline] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMacModal, setShowMacModal] = useState(false);
  const [macAddress, setMacAddress] = useState('');
  const [bindingMac, setBindingMac] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [usageError, setUsageError] = useState(null);

  const fetchLicense = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLicense(id);
      setLicense(data);
    } catch (error) {
      console.error('Error fetching license:', error);
      message.error('Failed to load license details');
      navigate('/admin/licenses');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch license activities
  const fetchActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const data = await getLicenseActivities(id);
      setActivities(data);
      setActivitiesError(null);
      return data;
    } catch (error) {
      console.error('Error fetching license activities:', error);
      setActivitiesError('Failed to load activities');
      return [];
    } finally {
      setLoadingActivities(false);
    }
  }, [id]);

  // Function to fetch usage data
  const fetchUsageData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoadingUsage(true);
      setUsageError(null);
      const data = await getLicenseUsage(id);
      setUsageData(data);
      return data;
    } catch (error) {
      console.error('Error fetching license usage:', error);
      setUsageError('Failed to load usage data');
      message.error('Failed to load usage statistics');
      return null;
    } finally {
      setLoadingUsage(false);
    }
  }, [id]);

  // Fetch license data, activities, and usage data
  useEffect(() => {
    const fetchData = async () => {
      await fetchLicense();
      if (id) {
        await Promise.all([
          fetchActivities(),
          fetchUsageData()
        ]);
      }
    };
    fetchData();
  }, [fetchLicense, fetchActivities, fetchUsageData, id]);

  // Fetch customer data when license changes
  useEffect(() => {
    const fetchCustomer = async () => {
      if (!license?.customer_licenses || license.customer_licenses.length === 0) {
        setCustomer(null);
        return;
      }

      const primaryCustomer = license.customer_licenses.find(cl => cl.is_primary) || license.customer_licenses[0];
      const customerId = primaryCustomer?.customer_id;

      if (!customerId) {
        setCustomerError('No customer ID found');
        return;
      }

      if (primaryCustomer.company_name) {
        setCustomer({ name: primaryCustomer.company_name });
        return;
      }

      setLoadingCustomer(true);
      try {
        const customerData = await customerService.getCustomer(customerId);
        console.log("customerData = " ,customerData);
        setCustomer(customerData);
        setCustomerError(null);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setCustomerError('Failed to load customer data');
      } finally {
        setLoadingCustomer(false);
      }
    };

    if (license) {
      fetchCustomer();
    }
  }, [license]);

  const fetchUsage = useCallback(async () => {
    try {
      const data = await getLicenseUsage(id);
      setUsage(data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      message.error('Failed to load usage data');
    }
  }, [id]);

  // Fetch usage data when on the usage tab
  useEffect(() => {
    if (activeTab === 'usage' && !usage) {
      fetchUsage();
    }
  }, [activeTab, fetchUsage, usage]);



  const handleDelete = async () => {
    if (!id) {
      console.error('No license ID provided for deletion');
      message.error('No license selected for deletion');
      return;
    }
    
    try {
      setDeleting(true);
      console.log('Initiating license deletion for ID:', id);
      
      const response = await deleteLicense(id);
      
      // Handle successful response
      if (response && response.success) {
        const successMessage = response.message || 'License deactivated successfully';
        console.log('License deletion successful:', { response, licenseId: id });
        message.success(successMessage);
        
        // Reset state and navigate back to licenses list
        setShowDeleteModal(false);
        navigate('/admin/licenses');
        
        return; // Exit early on success
      }
      
      // Handle unexpected but non-error response
      const errorMessage = response?.message || 'Failed to deactivate license (unexpected response)';
      message.error(errorMessage);
      
    } catch (error) {
      // Handle different error status codes
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response;
        console.error(`Backend returned status ${status}`, data);
        
        if (status === 401) {
          // Session expired or not logged in
          message.error('Your session has expired. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 1500);
          return;
        } else if (status === 403) {
          // Permission denied
          message.error('You do not have permission to delete licenses. Only administrators can perform this action.');
        } else if (status === 404) {
          // License not found
          message.error('The specified license was not found or has already been deleted.');
        } else if (status === 400) {
          // Bad request (e.g., active customers)
          const errorMessage = data?.detail || 'Cannot delete license. It may have active customer assignments.';
          message.error(errorMessage);
        } else if (status >= 500) {
          // Server error
          message.error('A server error occurred while deleting the license. Our team has been notified. Please try again later.');
        } else {
          // Other 4xx/5xx errors
          const errorMessage = data?.detail || data?.message || 'An unexpected error occurred';
          message.error(`Error: ${errorMessage}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received from server', error.request);
        message.error('Unable to connect to the server. Please check your network connection and try again.');
      } else if (error.message === 'Network Error') {
        // Network error (e.g., CORS, offline)
        message.error('Network error. Please check your internet connection and try again.');
      } else if (error.message && error.message.includes('timeout')) {
        // Request timeout
        message.error('Request timed out. The server is taking too long to respond. Please try again.');
      } else {
        // Other errors (e.g., errors in the code)
        console.error('Unexpected error:', error);
        message.error({
          content: error.message || 'An unexpected error occurred',
          duration: 5,
          style: { whiteSpace: 'pre-line' }
        });
      }
      
    } finally {
      setDeleting(false);
    }
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      const result = await validateLicense(license.license_key);
      setValidationResult(result);
      message.success('License validated successfully');
    } catch (error) {
      console.error('Error validating license:', error);
      message.error(`Validation failed: ${error.message}`);
    } finally {
      setValidating(false);
    }
  };

  const handleGenerateOffline = async () => {
    try {
      setGeneratingOffline(true);
      await generateOfflineLicense(id);
      
      // In a real app, you would trigger a file download here
    } catch (error) {
      console.error('Error generating offline license:', error);
      message.error('Failed to generate offline license');
    } finally {
      setGeneratingOffline(false);
    }
  };

  const handleBindMac = async () => {
    if (!macAddress) {
      message.warning('Please enter a valid MAC address');
      return;
    }
    
    // Basic MAC address validation (simple format check)
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    if (!macRegex.test(macAddress)) {
      message.error('Please enter a valid MAC address (format: 00:1A:2B:3C:4D:5E)');
      return;
    }

    try {
      setBindingMac(true);
      await bindMacAddress(id, macAddress);
      message.success(`MAC address ${macAddress} bound successfully`);
      setShowMacModal(false);
      setMacAddress('');
      fetchLicense(); // Refresh license data
    } catch (error) {
      console.error('Error binding MAC address:', error);
      const errorMessage = error.response?.data?.message || 'Failed to bind MAC address';
      message.error(errorMessage);
    } finally {
      setBindingMac(false);
    }
  };

  const handleRevokeLicense = async () => {
    if (!license) return;
    
    try {
      setRevoking(true);
      const response = await revokeLicense(license.id);
      
      if (response) {
        // Handle successful revocation
        const successMessage = response.message || 'License has been successfully revoked';
        message.success(successMessage);
        
        // Refresh the license data to show updated status
        await fetchLicense();
      }
    } catch (error) {
      console.error('Error revoking license:', error);
      message.error(error.response?.data?.message || 'Failed to revoke license');
    } finally {
      setRevoking(false);
    }
  };

  const handleSuspendLicense = async () => {
    if (!license) return;
    
    try {
      setSuspending(true);
      // Update the license status to SUSPENDED
      const response = await updateLicense(license.id, { status: 'SUSPENDED' });
      
      if (response) {
        message.success('License has been suspended successfully');
        // Refresh the license data to show updated status
        await fetchLicense();
      }
    } catch (error) {
      console.error('Error suspending license:', error);
      message.error(error.response?.data?.message || 'Failed to suspend license');
    } finally {
      setSuspending(false);
    }
  };

  const showMacRequestModal = () => {
    setMacAddress('');
    setShowMacModal(true);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      [LICENSE_STATUS.ACTIVE]: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: 'Active'
      },
      [LICENSE_STATUS.REVOKED]: {
        color: 'error',
        icon: <StopOutlined />,
        text: 'Revoked'
      },
      [LICENSE_STATUS.EXPIRED]: {
        color: 'error',
        icon: <StopOutlined />,
        text: 'Expired'
      },
      [LICENSE_STATUS.PENDING]: {
        color: 'warning',
        icon: <WarningOutlined />,
        text: 'Pending'
      },
      [LICENSE_STATUS.SUSPENDED]: {
        color: 'default',
        icon: <StopOutlined />,
        text: 'Suspended'
      }
    };

    const statusInfo = statusMap[status] || { color: 'default', text: status };
    
    return (
      <Tag 
        color={statusInfo.color} 
        icon={statusInfo.icon}
        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        {statusInfo.text}
      </Tag>
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  if (loading || !license) {
    return <div>Loading...</div>;
  }

  const isActive = license.status === LICENSE_STATUS.ACTIVE;
  const isExpired = license.status === LICENSE_STATUS.EXPIRED;

  return (
    <div className="license-detail">
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="link" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(-1)}
        >
          Back to Licenses
        </Button>
      </div>

      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>License Details</Title>
            {getStatusTag(license.status)}
          </Space>
        }
        extra={
          <Space>
            {isActive && (
              <>
                <Button 
                  type="primary" 
                  onClick={handleValidate}
                  loading={validating}
                  icon={<CheckCircleOutlined />}
                >
                  Validate
                </Button>
                <Button 
                  type="default" 
                  onClick={showMacRequestModal}
                  icon={<ReloadOutlined />}
                  style={{ marginLeft: 8 }}
                >
                  Mac Assign
                </Button>
              </>
            )}
            
            {isActive && (
              <Button 
                onClick={handleGenerateOffline}
                loading={generatingOffline}
                icon={<DownloadOutlined />}
              >
                Generate Offline
              </Button>
            )}
            {/* http://localhost:3000/admin/licenses/27/edit */}
            <Link to={`/admin/licenses/${id}/edit`}>
              <Button 
                icon={<EditOutlined />}
                disabled={license?.status === 'REVOKED' || license?.status === 'EXPIRED'}
              >
                Edit
              </Button>
            </Link>
            
            {isActive && (
              <>
                <Button 
                  type="default"
                  danger
                  icon={<PauseCircleOutlined />}
                  onClick={handleSuspendLicense}
                  loading={suspending}
                  style={{ marginRight: 8 }}
                >
                  Suspend
                </Button>
                <Button 
                  danger 
                  icon={<StopOutlined />} 
                  onClick={handleRevokeLicense}
                  loading={revoking}
                  disabled={license?.status === 'REVOKED' || license?.status === 'EXPIRED'}
                >
                  {license?.status === 'REVOKED' ? 'Revoked' : 'Revoke'}
                </Button>
              </>
            )}
            
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => setShowDeleteModal(true)}
              loading={deleting}
              disabled={license?.status === 'REVOKED' || license?.status === 'EXPIRED'}
            >
              Delete
            </Button>
            
            <Modal
              title="Bind MAC Address"
              open={showMacModal}
              onOk={handleBindMac}
              onCancel={() => setShowMacModal(false)}
              confirmLoading={bindingMac}
              okText="Bind MAC"
              cancelText="Cancel"
            >
              <p>Enter the MAC address to bind to this license:</p>
              <Form.Item
                  label="MAC Address"
                  name="mac_address"
                  rules={[{ required: true, message: 'Please enter a value' }]}
                  
                  onChange={(e) => setMacAddress(e.target.value)}
                >
                  <Input style={{ width: '100%' }} />
                </Form.Item>
              {/* <input
                type="text"
                className="ant-input "
                placeholder="00:1A:2B:3C:4D:5E"
                value={macAddress}
                onChange={(e) => setMacAddress(e.target.value)}
                style={{ width: '100%', padding: '8px' }}
              /> */}
              <p style={{ marginTop: '8px', color: '#888', fontSize: '12px' }}>
                Format: 00:1A:2B:3C:4D:5E
              </p>
            </Modal>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Overview" key="overview">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Card title="License Information" size="small">
                  <Descriptions column={1} bordered>
                    <Descriptions.Item label="License Key">
                      <Space>
                        <code>{license.license_key}</code>
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<CopyOutlined />} 
                          onClick={() => copyToClipboard(license.license_key)}
                        />
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Type">
                      <Tag color={license.license_type === LICENSE_TYPES.SUBSCRIPTION ? 'purple' : 'blue'}>
                        {license.license_type.charAt(0).toUpperCase() + license.license_type.slice(1)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer">
                      {loadingCustomer ? (
                        <div style={{ width: '150px' }}>
                          <Skeleton.Input active size="small" block />
                        </div>
                      ) : customerError ? (
                        <Tooltip title={customerError}>
                          <span>
                            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                            Error Loading
                          </span>
                        </Tooltip>
                      ) : customer ? (
                        <div>
                          <div>
                            <Link to={`/admin/customers/${customer.id}`}>
                              {customer.contact_name || customer.company_name || 'Unnamed Customer'}
                            </Link>
                          </div>
                          {customer.company_email && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              {customer.company_email}
                            </div>
                          )}
                          {customer.company_phone && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {customer.company_phone}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#8c8c8c' }}>Unassigned</span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                      {moment(license.created_at).format('lll')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated">
                      {moment(license.updated_at).fromNow()}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              
              <Col xs={24} md={12}>
                <Card title="Validity" size="small">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Statistic 
                        title="Activation Date" 
                        value={moment(license.activation_date).format('ll')}
                      />
                    </Col>
                    <Col xs={24} sm={12}>
                      <Statistic 
                        title="Expiry Date" 
                        value={moment(license.expiry_date).format('ll')}
                        valueStyle={isExpired ? { color: '#ff4d4f' } : {}}
                      />
                    </Col>
                    <Col xs={24} sm={12}>
                      <Statistic 
                        title="Days Remaining" 
                        value={moment(license.expiry_date).diff(moment(), 'days')}
                        suffix="days"
                      />
                    </Col>
                    <Col xs={24} sm={12}>
                      <Statistic 
                        title="Grace Period" 
                        value={license.grace_period_days}
                        suffix="days"
                      />
                    </Col>
                  </Row>
                  
                  {license.mac_address && (
                    <div style={{ marginTop: 16 }}>
                      <Divider orientation="left">Device Binding</Divider>
                      <Space>
                        <Text>MAC Address: <Text code>{license.mac_address}</Text></Text>
                        <Button 
                          size="small" 
                          onClick={handleBindMac}
                          icon={<ReloadOutlined />}
                        >
                          Rebind
                        </Button>
                      </Space>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
            
            {validationResult && (
              <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card 
                    title="Validation Result" 
                    size="small"
                    extra={
                      <Tag 
                        color={validationResult.valid ? 'success' : 'error'}
                        icon={validationResult.valid ? <CheckCircleOutlined /> : <WarningOutlined />}
                      >
                        {validationResult.valid ? 'Valid' : 'Invalid'}
                      </Tag>
                    }
                  >
                    <Descriptions column={1} bordered>
                      <Descriptions.Item label="Status">
                        {validationResult.status}
                      </Descriptions.Item>
                      <Descriptions.Item label="Message">
                        {validationResult.message}
                      </Descriptions.Item>
                      <Descriptions.Item label="License ID">
                        {validationResult.license_id}
                      </Descriptions.Item>
                      <Descriptions.Item label="Next Validation">
                        {moment(validationResult.next_validation).format('lll')}
                      </Descriptions.Item>
                      <Descriptions.Item label="Valid Until">
                        {validationResult.valid_until ? moment(validationResult.valid_until).format('lll') : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="License File">
                        {validationResult.license_file_path || 'Not generated'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              </Row>
            )}

            <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card title="Usage Restrictions" size="small">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic 
                        title="Max Companies" 
                        value={license.max_companies}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic 
                        title="Max Active Employees" 
                        value={license.max_active_employees}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                      <Statistic 
                        title="Max Users" 
                        value={license.max_users}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            
            
            
            {license.notes && (
              <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card title="Notes" size="small">
                    <Paragraph>{license.notes}</Paragraph>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>
          
          <TabPane tab="Usage" key="usage">
            {loadingUsage ? (
              <Skeleton active />
            ) : usageError ? (
              <Alert message={usageError} type="error" />
            ) : usageData ? (
              <div className="usage-metrics">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small" title="Total Activations">
                      <Statistic
                        value={usageData.total_activations || 0}
                        valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                      />
                      <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                        {usageData.last_activation_date 
                          ? `Last: ${moment(usageData.last_activation_date).format('YYYY-MM-DD')}` 
                          : 'No activations recorded'}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small" title="Active Sessions">
                      <Statistic
                        value={usageData.active_sessions || 0}
                        valueStyle={{ 
                          color: usageData.active_sessions > 0 ? '#52c41a' : '#666',
                          fontSize: '24px',
                          fontWeight: 'bold' 
                        }}
                      />
                      <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                        {usageData.max_concurrent_sessions 
                          ? `Max concurrent: ${usageData.max_concurrent_sessions}` 
                          : 'No session data'}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small" title="API Usage">
                      <Statistic
                        value={usageData.api_calls_count || 0}
                        valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                      />
                      <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                        {usageData.last_api_call 
                          ? `Last call: ${moment(usageData.last_api_call).fromNow()}` 
                          : 'No API calls recorded'}
                      </div>
                    </Card>
                  </Col>
                </Row>
                
                {usageData.feature_usage && usageData.feature_usage.length > 0 && (
                  <Card 
                    size="small" 
                    title="Feature Usage" 
                    style={{ marginTop: 16 }}
                  >
                    <Row gutter={[16, 24]}>
                      {usageData.feature_usage.map((feature, index) => (
                        <Col xs={24} sm={12} md={8} key={index}>
                          <div className="feature-usage-item">
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              marginBottom: 4
                            }}>
                              <span style={{ fontWeight: 500 }}>{feature.feature}</span>
                              <span style={{ fontWeight: 'bold' }}>
                                {feature.used} / {feature.limit || '∞'}
                              </span>
                            </div>
                            <div style={{ 
                              width: '100%', 
                              backgroundColor: '#f0f0f0', 
                              borderRadius: 4,
                              overflow: 'hidden'
                            }}>
                              <div 
                                style={{
                                  width: `${feature.percentage}%`,
                                  height: 8,
                                  backgroundColor: feature.percentage > 90 ? '#ff4d4f' : 
                                                 feature.percentage > 70 ? '#faad14' : '#52c41a',
                                  transition: 'all 0.3s',
                                  borderRadius: 4
                                }}
                              />
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              marginTop: 4,
                              fontSize: 12,
                              color: '#666'
                            }}>
                              <span>{feature.percentage}% utilized</span>
                              {feature.limit && (
                                <span>{feature.limit - feature.used} remaining</span>
                              )}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                )}
                
                {usageData.usage_history && usageData.usage_history.length > 0 && (
                  <Card 
                    size="small" 
                    title="Usage History (Last 30 days)" 
                    style={{ marginTop: 16 }}
                  >
                    <div style={{ height: '250px' }}>
                      {/* This would typically be a chart component */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-end', 
                        height: '200px',
                        padding: '16px 0',
                        borderBottom: '1px solid #f0f0f0',
                        gap: '4px',
                        justifyContent: 'space-around'
                      }}>
                        {usageData.usage_history.map((day, index) => (
                          <div 
                            key={index}
                            style={{
                              width: '20px',
                              height: `${Math.min(day.count * 2, 100)}%`,
                              backgroundColor: '#1890ff',
                              borderRadius: '4px 4px 0 0',
                              position: 'relative'
                            }}
                            title={`${day.date}: ${day.count} activations`}
                          >
                            <div style={{
                              position: 'absolute',
                              bottom: '-20px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '10px',
                              whiteSpace: 'nowrap',
                              color: '#666'
                            }}>
                              {day?.date?.split('-')[2] || ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            ) : (
              <Empty description="No usage data available" />
            )}
          </TabPane>
          
          <TabPane tab="Activity" key="activity">
            <Card 
              size="small"
              title={
                <Space>
                  <span>Activity Log</span>
                  <Button 
                    icon={<ReloadOutlined />} 
                    size="small" 
                    onClick={fetchActivities}
                    loading={loadingActivities}
                  >
                    Refresh
                  </Button>
                </Space>
              }
            >
              {activitiesError ? (
                <Alert
                  message="Error loading activities"
                  description={activitiesError}
                  type="error"
                  showIcon
                  action={
                    <Button size="small" danger onClick={fetchActivities}>
                      Retry
                    </Button>
                  }
                />
              ) : loadingActivities && activities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ) : activities.length === 0 ? (
                <Empty description="No activity found" />
              ) : (
                <Timeline mode="left">
                  {activities.map((activity) => {
                    // Map activity type to color and icon
                    const activityConfig = {
                      'CREATION': { color: 'green', icon: <FileAddOutlined /> },
                      'ACTIVATION': { color: 'green', icon: <CheckCircleOutlined /> },
                      'MAC_BIND': { color: 'blue', icon: <LinkOutlined /> },
                      'VALIDATION': { color: 'blue', icon: <SafetyCertificateOutlined /> },
                      'REVOCATION': { color: 'red', icon: <StopOutlined /> },
                      'EXPIRATION': { color: 'orange', icon: <ClockCircleOutlined /> },
                      'SUSPENSION': { color: 'orange', icon: <PauseCircleOutlined /> },
                      'CUSTOMER_ASSIGNMENT': { color: 'purple', icon: <UserAddOutlined /> },
                      'UPDATE': { color: 'gray', icon: <EditOutlined /> },
                    }[activity.type] || { color: 'gray', icon: <InfoCircleOutlined /> };

                    return (
                      <Timeline.Item 
                        key={activity.id}
                        color={activityConfig.color}
                        dot={activityConfig.icon}
                        label={moment(activity.timestamp).format('lll')}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <strong style={{ marginRight: 8 }}>
                            {activity.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </strong>
                          {activity.user && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                              {activity.user.name || 'System'}
                            </Tag>
                          )}
                          {activity.system && (
                            <Tag color="default" style={{ marginLeft: 8 }}>
                              System
                            </Tag>
                          )}
                        </div>
                        
                        {/* Render details based on activity type */}
                        {activity.type === 'CREATION' && (
                          <div>License created with key: <code>{activity.details.license_key}</code></div>
                        )}
                        
                        {activity.type === 'ACTIVATION' && (
                          <div>Activated by: {activity.details.activated_by || 'System'}</div>
                        )}
                        
                        {activity.type === 'MAC_BIND' && (
                          <div>
                            <div>MAC Address: <code>{activity.details.mac_address}</code></div>
                            {activity.details.change_count > 0 && (
                              <div>Change count: {activity.details.change_count}</div>
                            )}
                          </div>
                        )}
                        
                        {activity.type === 'VALIDATION' && (
                          <div>
                            <div>IP: {activity.details.ip_address || 'N/A'}</div>
                            <div>Total validations: {activity.details.attempt_count || 1}</div>
                          </div>
                        )}
                        
                        {activity.type === 'REVOCATION' && (
                          <div>
                            {activity.details.revoked_by && (
                              <div>Revoked by: {activity.details.revoked_by}</div>
                            )}
                            {activity.details.reason && (
                              <div>Reason: {activity.details.reason}</div>
                            )}
                          </div>
                        )}
                        
                        {activity.type === 'EXPIRATION' && (
                          <div>
                            <div>Expired on: {moment(activity.timestamp).format('lll')}</div>
                            {activity.details.grace_period_days > 0 && (
                              <div>Grace period: {activity.details.grace_period_days} days</div>
                            )}
                          </div>
                        )}
                        
                        {activity.type === 'CUSTOMER_ASSIGNMENT' && (
                          <div>
                            <div>Customer: {activity.details.customer_name}</div>
                            {activity.details.is_primary && (
                              <Tag color="blue">Primary</Tag>
                            )}
                          </div>
                        )}
                        
                        <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                          {moment(activity.timestamp).fromNow()}
                        </div>
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              )}
            </Card>
          </TabPane>
          
          {/* <TabPane tab="Usage" key="usage">
            {usage ? (
              <Card size="small">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic 
                      title="Companies Used" 
                      value={usage.companies_used}
                      suffix={`/ ${license.max_companies}`}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic 
                      title="Active Employees" 
                      value={usage.active_employees_used}
                      suffix={`/ ${license.max_active_employees}`}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic 
                      title="Users" 
                      value={usage.users_used}
                      suffix={`/ ${license.max_users}`}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic 
                      title="ESS Users" 
                      value={usage.ess_users_used}
                      suffix={`/ ${license.max_ess_users}`}
                    />
                  </Col>
                </Row>
                
                <Divider orientation="left">Last Updated</Divider>
                <Text type="secondary">
                  {moment(usage.last_updated).format('lll')} ({moment(usage.last_updated).fromNow()})
                </Text>
              </Card>
            ) : (
              <div>Loading usage data...</div>
            )}
          </TabPane> */}
          
          <TabPane tab="Validation" key="validation">
            <Card size="small">
              <Button 
                type="primary" 
                onClick={handleValidate}
                loading={validating}
                icon={<CheckCircleOutlined />}
                style={{ marginBottom: 16 }}
              >
                Validate License
              </Button>
              
              {validationResult ? (
                <div style={{ marginTop: 16 }}>
                  <Card 
                    title="Validation Details" 
                    size="small"
                    extra={
                      <Tag 
                        color={validationResult.valid ? 'success' : 'error'}
                        icon={validationResult.valid ? <CheckCircleOutlined /> : <WarningOutlined />}
                      >
                        {validationResult.valid ? 'Valid' : 'Invalid'}
                      </Tag>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Card size="small" title="Status Information">
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="Status">
                              <Tag color={validationResult.valid ? 'success' : 'error'}>
                                {validationResult.status || 'N/A'}
                              </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Message">
                              {validationResult.message || 'No message available'}
                            </Descriptions.Item>
                            <Descriptions.Item label="License ID">
                              {validationResult.license_key || 'N/A'}
                            </Descriptions.Item>
                          </Descriptions>
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <Card size="small" title="Validity Period">
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="Valid Until">
                              {validationResult.valid_until 
                                ? moment(validationResult.valid_until).format('lll') 
                                : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Next Validation">
                              {validationResult.next_validation 
                                ? moment(validationResult.next_validation).fromNow() 
                                : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Last Checked">
                              {moment().format('lll')}
                            </Descriptions.Item>
                          </Descriptions>
                        </Card>
                      </Col>
                    </Row>
                    
                    {validationResult.restrictions && (
                      <div style={{ marginTop: 16 }}>
                        <Divider orientation="left" style={{ marginTop: 0 }}>License Restrictions</Divider>
                        <Row gutter={[16, 16]}>
                          {Object.entries(validationResult.restrictions).map(([key, value]) => (
                            <Col xs={12} sm={8} md={6} key={key}>
                              <Statistic 
                                title={key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                value={value}
                              />
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </Card>
                </div>
              ) : (
                <Alert 
                  message="No validation data available" 
                  description="Click the 'Validate License' button to perform a validation check."
                  type="info"
                  showIcon
                />
              )}
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
        confirmLoading={deleting}
        okText="Yes, Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this license? This action cannot be undone.</p>
        {license?.license_key && (
          <p style={{ fontWeight: 'bold', marginTop: '10px' }}>
            License Key: <code>{license.license_key}</code>
          </p>
        )}
      </Modal>
    </div>
  );
};

export default LicenseDetail;
